import { ActionSheetProvider } from "@expo/react-native-action-sheet";
import {
  ApolloProvider,
  isNative,
  PostHogProvider,
  useBrowser,
  useCheckIfProfileExistsQuery,
  useGetUserProfileQuery,
  useInitApolloClient,
  useIsAppActive,
  useNeedToUpgradeApp,
  useSendExpoToken,
} from "@stepapp/core";
import { AppTypeEnum } from "@stepapp/core/apiTypes";
import { StripeProvider } from "@stepapp/stripe";
import {
  Block,
  DrawerLayout,
  palette,
  SafeAreaProvider,
  SafeAreaView,
  spacing,
  useBreakpoints,
  useFontsLoad,
} from "@stepapp/ui";
import { CioLogLevel, CioRegion, CustomerIO } from "customerio-reactnative";
import { applicationId, nativeApplicationVersion } from "expo-application";
import { setBadgeCountAsync } from "expo-notifications";
import { usePathname, useRouter } from "expo-router";
import * as SplashScreen from "expo-splash-screen";
import { StatusBar } from "expo-status-bar";
import * as Updates from "expo-updates";
import { memo, type PropsWithChildren, useEffect, useMemo } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import {
  Alert,
  LogBox,
  Modal,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
} from "react-native";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { enableScreens } from "react-native-screens";
import { Provider as ReduxProvider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import {
  BottomNavigation,
  DialogModal,
  Drawer,
  HomeHeader,
  InAppNotification,
  inAppNotificationRef,
  Loader,
  loaderRef,
} from "../components";
import { useSetupApp } from "../config/setupApp";
import { getApiUrl, TEST_OR_STAGING } from "../config/urls";
import { drawerRef } from "../helpers/drawer";
import { DialogModalProvider, useAppDispatch, useAppLinks } from "../hooks";
import { AppStorage } from "../services/AppStorage";
import { i18n } from "../services/I18n";
import { handleIntercomPushMessage, IntercomProvider } from "../services/Intercom";
import {
  handleInitialNotification,
  useRegisterPushNotifications,
} from "../services/pushNotifications";
import store, { persistedStore } from "../store";
import { setUserData } from "../store/user";
import { AppContent } from "./AppContent";

// @ts-ignore
TouchableOpacity.defaultProps = {
  activeOpacity: 0.6,
};

// @ts-ignore
ScrollView.defaultProps = {
  keyboardDismissMode: "on-drag",
  showsVerticalScrollIndicator: false,
};

// @ts-ignore
Modal.defaultProps = {
  backdropTransitionOutTiming: 0,
  statusBarTranslucent: true,
};

// @ts-ignore
TextInput.defaultProps = {
  placeholderTextColor: palette.font.placeholder,
  underlineColorAndroid: "transparent",
};

if (__DEV__) {
  LogBox.ignoreLogs([
    "Layout children must be of type Screen, all other children are ignored.",
    'You seem to update props of the "TRenderEngine',
  ]);
}

enableScreens();
SplashScreen.preventAutoHideAsync();

const SessionManager = ({ children }: PropsWithChildren) => {
  const { isUpdateAvailable } = Updates.useUpdates();
  const { navigate } = useRouter();
  const { t } = useTranslation();
  const isAppActive = useIsAppActive();
  const { AppCustomer } = useAppLinks();

  const dispatch = useAppDispatch();

  const sendExpoToken = useSendExpoToken();
  const needToUpgradeVersion = useNeedToUpgradeApp({
    version: nativeApplicationVersion,
    appType: AppTypeEnum.CustomerApp,
  });

  const { data: profileExists } = useCheckIfProfileExistsQuery({
    skip: !AppStorage().get("refreshToken"),
  });

  const { data } = useGetUserProfileQuery({
    variables: { isCustomerProfile: true },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    skip: !profileExists?.checkIfProfileExists,
    onCompleted: () => {
      handleInitialNotification(navigate);
    },
  });

  useSetupApp();
  useRegisterPushNotifications();

  useEffect(() => {
    if (isAppActive) {
      setBadgeCountAsync(0);
      handleIntercomPushMessage();
    }
  }, [isAppActive]);

  useEffect(() => {
    if (data?.getMyProfile) {
      sendExpoToken();
      dispatch(setUserData(data?.getMyProfile));

      if (isNative) {
        CustomerIO.identify({
          userId: data?.getMyProfile?.id,
          traits: {
            firstName: data?.getMyProfile?.profileInformation?.firstName,
            lastName: data?.getMyProfile?.profileInformation?.lastName,
            email: data?.getMyProfile?.profileInformation?.email,
            phone: data?.getMyProfile?.profileInformation?.phone,
          },
        });
      }
    }
  }, [data, dispatch, sendExpoToken]);

  const openApplication = useBrowser(AppCustomer);

  useEffect(() => {
    if (needToUpgradeVersion && isAppActive) {
      setTimeout(
        () =>
          Alert.alert(
            t("alerts.updateTitle"),
            t("alerts.mandatoryUpdateMessage"),
            [{ onPress: openApplication, text: t("alerts.update") }],
            { cancelable: false },
          ),
        2500,
      );
    }
  }, [needToUpgradeVersion, isAppActive, openApplication, t]);

  useEffect(() => {
    if (__DEV__) return;

    if (isUpdateAvailable) {
      Updates.fetchUpdateAsync();
    }
  }, [isUpdateAvailable]);

  return <>{children}</>;
};

export const AppProviders = memo(() => {
  const { md } = useBreakpoints();
  const pathname = usePathname();
  const loaded = useFontsLoad();
  const apolloClient = useInitApolloClient({
    AppStorage,
    addSentryBreadcrumb: () => {},
    appType: AppTypeEnum.CustomerApp,
    captureSentryException: () => {},
    skipOnPathname: pathname !== "/welcome",
    url: getApiUrl(),
  });

  useEffect(() => {
    if (loaded) {
      SplashScreen.hideAsync();

      if (isNative) {
        CustomerIO.initialize({
          logLevel: CioLogLevel.Error,
          region: CioRegion.EU,
          trackApplicationLifecycleEvents: true,
          cdpApiKey: "d836000912039aeac629",
          inApp: { siteId: "faef52339b3d040b1613" },
        });
        CustomerIO.pushMessaging.showPromptForPushNotifications({
          ios: {
            sound: true,
            badge: true,
          },
        });
      }
    }
  }, [loaded]);

  const edges = useMemo(
    () => ["top", "left", "right"] as ("top" | "bottom" | "left" | "right")[],
    [],
  );

  const LeftAndRightEdge = useMemo(
    () => ["left", "right"] as ("top" | "bottom" | "left" | "right")[],
    [],
  );

  const applyAccentBackground = useMemo(
    () => ["/app-changelog", "/maintenance"].includes(pathname),
    [pathname],
  );

  const hideHomeHeader = useMemo(
    () =>
      ["welcome", "app-changelog", "/maintenance", "reviews/frame"].some((segment) =>
        pathname.includes(segment),
      ),
    [pathname],
  );

  const hideTopEdge = useMemo(() => pathname.includes("welcome"), [pathname]);

  if (!(loaded && apolloClient)) return <Block />;

  return (
    <PostHogProvider apiKey="phc_eX6gxek9iBwR6YuxF64N8ltHkImMqYEVJHtZbkPKoxo">
      <GestureHandlerRootView style={styles.container}>
        <ApolloProvider client={apolloClient}>
          <ReduxProvider store={store}>
            <PersistGate persistor={persistedStore}>
              <SafeAreaProvider>
                <DialogModalProvider>
                  <ActionSheetProvider>
                    <StripeProvider
                      test={TEST_OR_STAGING}
                      countryCode={AppStorage().get("countryCode")}
                      merchantIdentifier="merchant.stepapp.pl"
                      urlScheme={applicationId || "stepapp-customer"}
                    >
                      <IntercomProvider>
                        <I18nextProvider i18n={i18n}>
                          <SessionManager>
                            <StatusBar backgroundColor={palette.common.overlay} style="dark" />

                            <SafeAreaView
                              edges={hideTopEdge ? LeftAndRightEdge : edges}
                              style={[
                                styles.container,
                                {
                                  backgroundColor: applyAccentBackground
                                    ? palette.primary.accent
                                    : palette.common.white,
                                },
                              ]}
                            >
                              <DrawerLayout
                                contentContainerStyle={[
                                  styles.drawerContainer,
                                  !hideHomeHeader && styles.additionalPadding,
                                ]}
                                drawerPosition="left"
                                drawerWidth={spacing.deviceWidth}
                                overlayColor={`${palette.common.black}44`}
                                ref={drawerRef}
                                renderNavigationView={(progress) => <Drawer progress={progress} />}
                              >
                                {!hideHomeHeader && <HomeHeader />}

                                <AppContent />

                                {!md && (
                                  <Block alignCenter style={styles.bottomNavigation}>
                                    <BottomNavigation />
                                  </Block>
                                )}
                              </DrawerLayout>
                            </SafeAreaView>

                            <InAppNotification ref={inAppNotificationRef} />

                            <Loader ref={loaderRef} />

                            <DialogModal />
                          </SessionManager>
                        </I18nextProvider>
                      </IntercomProvider>
                    </StripeProvider>
                  </ActionSheetProvider>
                </DialogModalProvider>
              </SafeAreaProvider>
            </PersistGate>
          </ReduxProvider>
        </ApolloProvider>
      </GestureHandlerRootView>
    </PostHogProvider>
  );
});

const styles = StyleSheet.create({
  additionalPadding: {
    paddingTop: spacing.unit * 10,
  },
  bottomNavigation: {
    bottom: spacing.unit * 2,
    left: 0,
    position: "absolute",
    right: 0,
  },
  container: {
    flex: 1,
  },
  drawerContainer: {
    flex: 1,
  },
});
